/**
 * formatQuoteForClipboard — clean plain-text formatter for the
 * Missing-Stickers "Copy Quote" button on the Review-Quote screen.
 *
 * Consumes the SAME approved quote snapshot that renders on-screen
 * (totals + grouped priced lines + business meta + selected delivery).
 * Never recalculates prices, tax, or line totals — the frozen snapshot
 * is the single source of truth.
 *
 * Rules enforced by this formatter (see Copy-Quote spec):
 *   · Starts with `ORDER INVOICE`.
 *   · One sticker per line, format `CODE — $PRICE` (em-dash separator).
 *   · No decorative dividers, no box-drawing characters, no stars,
 *     no arrows, no dotted price-leaders, no spacing columns, no tabs.
 *   · Empty categories are skipped.
 *   · Every selected sticker code + duplicate + quantity is preserved.
 *   · Category assignments come straight from the grouped snapshot.
 *   · Subtotal, sales tax, and final total are printed exactly as
 *     provided by the snapshot.
 *   · Payment block includes the Zelle recipient and the payment
 *     disclaimer, verbatim per spec.
 *   · Delivery block reflects ONLY the selected delivery method.
 *   · Quote ID is printed at the bottom when available.
 */
const PRODUCT_NAME = "Panini FIFA World Cup 2026";
const PAYMENT_DISCLAIMER =
  "Once payment is received, the order cannot be changed or " +
  "canceled because the stickers are picked immediately.";

// P3.2 · Canonical variant vocabulary + copy-friendly labels.
export const COPY_VARIANT_LABELS = {
  white:        "WHITE",
  blue:         "BLUE PARALLEL",
  gold_crumple: "GOLD CRUMPLE",
  red:          "RED PARALLEL",
  orange:       "ORANGE PARALLEL",
  purple:       "PURPLE PARALLEL",
};

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

/**
 * @param {object}   args
 * @param {object}   args.totals              - Frozen `totals` object from the quote snapshot.
 * @param {Array}    args.groups              - Grouped priced lines (from `groupLinesByCategory`).
 * @param {object}   [args.meta]              - Business meta (zelle_phone, zelle_name, pickup_city).
 * @param {string}   args.deliveryCode        - "local_pickup" | shipping code.
 * @param {string}   [args.shippingLabel]     - Human label of the shipping method.
 * @param {number}   [args.shippingCost]      - Shipping cost in USD.
 * @param {string}   [args.sourceQuoteId]     - The Quote ID (e.g. QT-26-9V3QQ).
 * @param {string}   [args.productName]       - Overrides the default product line.
 * @param {string[]} [args.soldOutCodes]      - Phase B · deduplicated list of
 *                                             sticker codes that remain SELECTED
 *                                             AND are currently sold out. When
 *                                             non-empty, an AVAILABILITY section
 *                                             is emitted after the DELIVERY
 *                                             block. When empty / undefined, no
 *                                             section is added — matching the
 *                                             pre-Phase-B output byte for byte
 *                                             so existing clipboard tests stay
 *                                             green.
 * @returns {string} plain-text clipboard payload.
 */
export function formatQuoteForClipboard({
  totals,
  groups,
  meta = {},
  deliveryCode,
  shippingLabel,
  shippingCost,
  sourceQuoteId,
  productName = PRODUCT_NAME,
  soldOutCodes,
  variant,
}) {
  const stickerCount = Number(totals?.sticker_count || 0);
  const subtotal     = Number(totals?.subtotal      || 0);
  const taxAmount    = Number(totals?.tax?.amount   || 0);
  const taxRatePct   = Number(totals?.tax?.rate     || 0) * 100;
  const grand        = Number(totals?.total         || 0);
  const shipCost     = Number(
    shippingCost != null ? shippingCost : (totals?.shipping?.cost || 0));

  const zellePhone = String(meta.zelle_phone || "").trim();
  const zelleName  = String(meta.zelle_name  || "").trim();
  const pickupCity = String(meta.pickup_city || "Miami Gardens").trim();

  const out = [];

  // ── Header ────────────────────────────────────────────────
  out.push("ORDER INVOICE");
  out.push("");                                       // blank after ORDER INVOICE
  out.push(`${stickerCount} Sticker${stickerCount === 1 ? "" : "s"}`);
  out.push(productName);
  // P3.2 · Explicit variant identity so a Blue Parallel (or Gold /
  // Red / Orange / Purple) quote can NEVER be mistaken for White.
  // WHITE gets a compact `VARIANT: WHITE` line to keep every copied
  // quote self-describing without inventing decoration.
  const canonicalVariant = String(variant || "white").toLowerCase();
  const variantLabel = COPY_VARIANT_LABELS[canonicalVariant]
    || COPY_VARIANT_LABELS.white;
  out.push(`VARIANT: ${variantLabel}`);
  out.push("");                                       // blank after product header

  // ── Category groups ──────────────────────────────────────
  const activeGroups = (groups || []).filter((g) => Number(g.qty) > 0);
  activeGroups.forEach((g, idx) => {
    out.push(g.label);
    const rows = (g.lines || []).filter((l) => {
      if (typeof g._isSelected === "function") return g._isSelected(l.code);
      return true;
    });
    for (const l of rows) {
      // Duplicates (qty > 1) are collapsed by the pricing engine into a
      // single line with `qty` + `line_total` already representing the
      // extended total. Presenting `CODE — $27.00` for a 3-copy line hid
      // the quantity/unit-price relationship from the customer. When
      // qty > 1 we render an explicit breakdown so the invoice line
      // communicates code · quantity · unit price · extended total.
      // Pricing math is unchanged — `line_total` from the frozen snapshot
      // is still the authoritative extended amount.
      const q    = Number(l.qty || 1);
      const ext  = Number(l.line_total || 0);
      const code = String(l.code).trim();
      if (q > 1) {
        const unit = ext / q;
        out.push(`${code} × ${q} @ ${money(unit)} = ${money(ext)}`);
      } else {
        out.push(`${code} — ${money(ext)}`);
      }
    }
    // Blank line BETWEEN categories, and one more blank line AFTER the
    // last category (i.e. before the totals block).
    out.push("");
    // No extra push needed — the trailing "" above already serves as
    // "blank line before totals" once the loop ends. Keep the loop
    // simple so we never emit consecutive blank lines.
    void idx;
  });

  // ── Totals ────────────────────────────────────────────────
  out.push(`Subtotal: ${money(subtotal)}`);
  if (shipCost > 0 && deliveryCode !== "local_pickup") {
    out.push(`Shipping (${shippingLabel || "UPS Ground"}): ${money(shipCost)}`);
  }
  if (taxAmount > 0) {
    out.push(`Sales Tax (${taxRatePct.toFixed(0)}%): ${money(taxAmount)}`);
  }
  out.push(`TOTAL: ${money(grand)}`);
  out.push("");                                       // blank before PAYMENT

  // ── Payment ───────────────────────────────────────────────
  out.push("PAYMENT");
  out.push("");                                       // blank after PAYMENT
  out.push("Zelle");
  if (zellePhone) out.push(zellePhone);
  if (zelleName)  out.push(zelleName);
  out.push("");                                       // blank before payment disclaimer
  out.push(PAYMENT_DISCLAIMER);
  out.push("");                                       // blank before DELIVERY

  // ── Delivery (only the selected method) ──────────────────
  out.push("DELIVERY");
  out.push("");                                       // blank after DELIVERY
  if (deliveryCode === "local_pickup") {
    out.push("📍 LOCAL PICKUP");
    out.push("20719 NW 2ND AVE");
    out.push("MIAMI, FL 33160");
    out.push("");
    out.push("🕒 REGULAR BUSINESS HOURS");
    out.push("MONDAY–FRIDAY: 10:00 AM–7:00 PM");
    out.push("");
    out.push("⚠️ TEMPORARY HOURS");
    out.push("TUESDAY AND FRIDAY: CLOSING AT 6:00 PM");
  } else {
    out.push(shippingLabel || "UPS Ground");
    out.push("Ships anywhere in the USA");
  }
  out.push("");                                       // blank before Thank you!

  // ── Availability (Phase B) ────────────────────────────────
  // Emitted ONLY when at least one selected sticker remains sold out.
  // Uses the frozen availability snapshot supplied by the caller —
  // never triggers a second pricing calculation. Deduplicated code
  // list; one code per line; no separators / tabs / bullets.
  const uniqueSoldOut = Array.from(
    new Set((soldOutCodes || []).map((c) => String(c).trim()).filter(Boolean))
  );
  if (uniqueSoldOut.length > 0) {
    out.push("AVAILABILITY");
    out.push("");
    out.push("Currently Sold Out:");
    for (const c of uniqueSoldOut) out.push(c);
    out.push("");
    out.push(
      "Some stickers in this request are currently sold out. " +
      "We will review availability and contact you regarding fulfillment."
    );
    out.push("");
  }

  // ── Footer ────────────────────────────────────────────────
  out.push("Thank you!");
  if (sourceQuoteId) {
    out.push("");                                     // blank before Quote ID
    out.push(`Quote ID: ${sourceQuoteId}`);
  }

  return out.join("\n");
}

export default formatQuoteForClipboard;
